import { db, type LiftXDatabase } from '../db';
import { isLive } from '../tombstone';
import type { ExerciseRecord } from '../types';

/**
 * The only code that reads or writes the `exercises` table.
 *
 * **Source-agnostic by construction.** Catalogue and user-created exercises
 * share one table with a `source` discriminator, so every query here returns
 * both and callers cannot accidentally build a feature that works for one and
 * not the other (Phase 0 §7.1). `source` is used only for a provenance label
 * and an optional "my exercises" filter.
 *
 * Phase 3 builds search, filtering and the flags. Creating and editing
 * exercises is Phase 4 and is deliberately absent.
 */

/**
 * How results are ordered.
 *
 * `name` is the default because a library is browsed alphabetically.
 * `recentlyUpdated` surfaces what you last favourited or edited, which is the
 * other question worth asking of your own library.
 */
export type ExerciseSort = 'name' | 'recentlyUpdated';

export interface ExerciseQuery {
  /** Free text. Matched against `nameSearch`, normalised the same way. */
  text?: string;
  /** Single-select per facet; facets combine (Phase 4A decision G). */
  bodyPart?: string;
  targetMuscle?: string;
  equipment?: string;
  source?: ExerciseRecord['source'];
  favouritesOnly?: boolean;
  /** Archived rows are excluded unless this is true. */
  includeArchived?: boolean;
  sort?: ExerciseSort;
  limit?: number;
}

/** One filter option and how many exercises currently carry it. */
export interface Facet {
  slug: string;
  count: number;
}

/**
 * The filter options actually present in the database.
 *
 * Derived from the rows rather than read from `taxonomy.json`, so a
 * user-created exercise's equipment shows up in the filters even though the
 * bundled taxonomy has never heard of it. Taxonomy supplies display *labels*;
 * this supplies what there is to filter *by*.
 */
export interface ExerciseFacets {
  bodyParts: Facet[];
  targetMuscles: Facet[];
  equipment: Facet[];
}

/**
 * The deterministic id of a catalogue exercise's `exerciseUserState` sync
 * row — "Multi-user identity architecture" review, round 2, §4/§10. Safe as
 * a shared literal across every account, exactly like
 * `builtInMeasurementTypeId`: `exerciseUserState`'s real identity is now
 * `(user_id, id)`, so two accounts computing the identical id for the same
 * catalogue exercise never collide — they're different rows by primary key.
 */
export function exerciseUserStateId(catalogueExerciseId: string): string {
  return `eus_${catalogueExerciseId}`;
}

/**
 * Does this catalogue exercise currently carry any user-owned overlay state
 * worth syncing? The exact field list is the one this session's own
 * production migration logic uses (review round 2, §6) — kept in this one
 * place so the client-side reconciler (`reconcileExerciseUserState`,
 * `../sync/syncEngine.ts`) and any future server-side migration reasoning
 * about "touched" catalogue rows can't drift apart.
 */
export function hasNonDefaultOverlayState(exercise: ExerciseRecord): boolean {
  return (
    exercise.isFavourite ||
    exercise.isArchived ||
    exercise.notes !== null ||
    exercise.tags.length > 0 ||
    exercise.hasManualOverride ||
    exercise.mediaIds.length > 0 ||
    exercise.defaultRestSeconds !== null ||
    exercise.defaultIncrementG !== null
  );
}

/**
 * Normalise text for searching. Must match the rule the catalogue build used,
 * so a query normalises to the same shape the stored `nameSearch` has.
 */
export function normaliseForSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

export interface ExerciseRepository {
  count(): Promise<number>;
  countBySource(source: ExerciseRecord['source']): Promise<number>;
  get(id: string): Promise<ExerciseRecord | undefined>;
  /**
   * Reads a row regardless of tombstone state — for callers resolving static,
   * source-agnostic metadata (e.g. `source`, for the bundled catalogue-GIF
   * fallback) about an exercise that a historical record still references but
   * that may since have been deleted. Never use this for anything that should
   * respect the user having deleted the exercise (search, counts, pickers).
   */
  getIncludingDeleted(id: string): Promise<ExerciseRecord | undefined>;
  search(query: ExerciseQuery): Promise<ExerciseRecord[]>;
  facets(): Promise<ExerciseFacets>;
  /** How many sessions or plans reference this exercise — Phase 4B delete confirmation (decision A). */
  usageCount(id: string): Promise<number>;
  setFavourite(id: string, isFavourite: boolean): Promise<void>;
  setArchived(id: string, isArchived: boolean): Promise<void>;
}

export function createExerciseRepository(database: LiftXDatabase = db): ExerciseRepository {
  return {
    async count(): Promise<number> {
      return await database.exercises.filter(isLive).count();
    },

    async countBySource(source: ExerciseRecord['source']): Promise<number> {
      return await database.exercises.where('source').equals(source).filter(isLive).count();
    },

    async get(id: string): Promise<ExerciseRecord | undefined> {
      const row = await database.exercises.get(id);
      return row === undefined || !isLive(row) ? undefined : row;
    },

    async getIncludingDeleted(id: string): Promise<ExerciseRecord | undefined> {
      return await database.exercises.get(id);
    },

    /**
     * Entirely local. There is no network call here and there never will be —
     * that is what makes search work in a gym basement (Phase 0 §19).
     */
    async search(query: ExerciseQuery): Promise<ExerciseRecord[]> {
      // Start from the most selective index available rather than always
      // scanning: on a phone holding 1,500 rows, "all exercises hitting
      // `chest`" should be an index lookup, not a filter over everything.
      let rows: ExerciseRecord[];

      if (query.bodyPart !== undefined) {
        rows = await database.exercises.where('bodyParts').equals(query.bodyPart).toArray();
      } else if (query.targetMuscle !== undefined) {
        rows = await database.exercises.where('targetMuscles').equals(query.targetMuscle).toArray();
      } else if (query.equipment !== undefined) {
        rows = await database.exercises.where('equipment').equals(query.equipment).toArray();
      } else if (query.source !== undefined) {
        rows = await database.exercises.where('source').equals(query.source).toArray();
      } else {
        rows = await database.exercises.toArray();
      }

      const needle = query.text === undefined ? null : normaliseForSearch(query.text);

      const filtered = rows.filter((row) => {
        if (!isLive(row)) return false;
        if (query.includeArchived !== true && row.isArchived) return false;
        if (query.favouritesOnly === true && !row.isFavourite) return false;
        if (query.source !== undefined && row.source !== query.source) return false;
        if (query.bodyPart !== undefined && !row.bodyParts.includes(query.bodyPart)) return false;
        if (query.targetMuscle !== undefined && !row.targetMuscles.includes(query.targetMuscle)) {
          return false;
        }
        if (query.equipment !== undefined && !row.equipment.includes(query.equipment)) return false;
        if (needle !== null && needle.length > 0 && !row.nameSearch.includes(needle)) return false;
        return true;
      });

      // Sorted on the stored (lower-case) name. Display capitalisation is a
      // render concern — sorting on the formatted name would be the same order
      // anyway, and would cost 1,500 string transforms per keystroke.
      if (query.sort === 'recentlyUpdated') {
        filtered.sort((a, b) => b.updatedAt - a.updatedAt || a.name.localeCompare(b.name));
      } else {
        filtered.sort((a, b) => a.name.localeCompare(b.name));
      }

      return query.limit === undefined ? filtered : filtered.slice(0, query.limit);
    },

    /**
     * Count every taxonomy value across all non-archived rows.
     *
     * One full pass rather than one indexed count per possible value: there are
     * ~90 distinct slugs across the three facets, so 90 index counts would be
     * far more work than a single scan of 1,500 rows. Archived exercises are
     * excluded so the counts match what the default list actually shows.
     */
    async facets(): Promise<ExerciseFacets> {
      const rows = await database.exercises.toArray();

      const bodyParts = new Map<string, number>();
      const targetMuscles = new Map<string, number>();
      const equipment = new Map<string, number>();

      const tally = (map: Map<string, number>, values: string[]): void => {
        for (const value of values) map.set(value, (map.get(value) ?? 0) + 1);
      };

      for (const row of rows) {
        if (!isLive(row) || row.isArchived) continue;
        tally(bodyParts, row.bodyParts);
        tally(targetMuscles, row.targetMuscles);
        tally(equipment, row.equipment);
      }

      const toSortedFacets = (map: Map<string, number>): Facet[] =>
        [...map.entries()]
          .map(([slug, count]) => ({ slug, count }))
          .sort((a, b) => a.slug.localeCompare(b.slug));

      return {
        bodyParts: toSortedFacets(bodyParts),
        targetMuscles: toSortedFacets(targetMuscles),
        equipment: toSortedFacets(equipment),
      };
    },

    /**
     * Counts real usage, not just the two tables that could theoretically
     * reference the exercise but that Phase 5/6 haven't populated yet — this
     * stays correct without changes once plans and sessions exist for real.
     */
    async usageCount(id: string): Promise<number> {
      const [sessionUses, planUses] = await Promise.all([
        database.sessionExercises.where('exerciseId').equals(id).filter(isLive).count(),
        database.planExercises.where('exerciseId').equals(id).filter(isLive).count(),
      ]);
      return sessionUses + planUses;
    },

    async setFavourite(id: string, isFavourite: boolean): Promise<void> {
      await database.exercises.update(id, { isFavourite, updatedAt: Date.now() });
    },

    async setArchived(id: string, isArchived: boolean): Promise<void> {
      await database.exercises.update(id, { isArchived, updatedAt: Date.now() });
    },
  };
}

export const exerciseRepository = createExerciseRepository();
